import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaCheck } from 'react-icons/fa6';
import { BookOpen } from 'lucide-react';

/**
 * MINERVA REFACTORED - Atribuições Completas
 * Premium Design v3.0 | Chumbo + Dourado
 * Last Updated: 2026-01-29
 */

interface Category {
  id: string;
  title: string;
  icon: string;
  items: string[];
}

interface TitulacaoCategory {
  id: string;
  area: string;
  items: string[];
}

const Atribuicoes: React.FC = () => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>('capilar');
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-scroll-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const categories: Category[] = [
    {
      id: 'capilar',
      title: 'Área Capilar',
      icon: '/icons/b1.png',
      items: [
        'Cabeleireiro Profissional',
        'Barbeiro',
        'Especialista em Prótese/Laces',
        'Barboterapeuta',
        'Terapeuta Capilar',
        'Cosmetólogo',
        'Penteadista',
        'Especialista em Cortes',
        'Colorimetrista',
        'Trancista Profissional',
        'Megahista (Alongamento Capilar)',
        'Escovista Profissional',
        'Especialista em Alisamento',
        'Assistente Capilar',
      ],
    },
    {
      id: 'unhas',
      title: 'Área de Unhas e Podologia',
      icon: '/icons/b2.png',
      items: [
        'Manicure/Pedicure Profissional',
        'Podólogo',
        'Designer de Unhas',
      ],
    },
    {
      id: 'facial',
      title: 'Área Facial e Estética',
      icon: '/icons/b3.png',
      items: [
        'Designer de Sobrancelhas',
        'Lash Designer (Extensão de Cílios)',
        'Micropigmentador',
        'Nanopigmentador',
        'Especialista em Microblading',
        'Micropigmentador Labial',
        'Dermopigmentador Reconstrutivo',
        'Especialista em Depilação/Epilação',
        'Especialista em Laserterapia',
        'Esteticista Facial',
        'Esteticista Corporal',
        'Maquiador Profissional',
      ],
    },
    {
      id: 'terapia',
      title: 'Área de Terapias Complementares',
      icon: '/icons/b4.png',
      items: [
        'Massoterapeuta',
        'Terapeuta Corporal Tântrico',
        'Massoterapeuta Esportivo',
        'Massoterapeuta Relaxante',
        'Especialista em Drenagem Linfática',
        'Especialista em Massagem Modeladora',
        'Massoterapeuta com Termoterapia',
        'Terapeuta de Mesoterapia',
        'Quiropraxista Profissional',
        'Terapeuta em Ozonioterapia',
        'Terapeuta Integrativo',
        'Terapeuta Holístico',
        'Terapeuta em Terapias Naturais',
        'Terapeuta em Shiatsu',
        'Terapeuta em Reflexologia',
        'Especialista em Bronzeamento Natural',
        'Terapeuta em Aromaterapia',
        'Terapeuta em Reiki',
        'Terapeuta Auriculoterapeuta',
        'Terapeuta em Cromoterapia',
        'Terapeuta em Ventosaterapia',
        'Terapeuta em Fitoterapia',
        'Terapeuta em Geoterapia',
        'Terapeuta em Acupuntura Estética',
        'Terapeuta em Meditação Guiada',
      ],
    },
    {
      id: 'gestao',
      title: 'Gestão e Biossegurança',
      icon: '/icons/b5.png',
      items: [
        'Gestor de Salão ou Instituição de Ensino',
        'Técnico em Biossegurança',
      ],
    },
    {
      id: 'arte',
      title: 'Área Artística',
      icon: '/icons/b6.png',
      items: [
        'Tatuador Profissional (Certificação ANVISA)',
      ],
    },
  ];

  const titulacoes: TitulacaoCategory[] = [
    {
      id: 'capilar',
      area: 'ÁREA CAPILAR',
      items: [
        'Mestre Barbeiro Educador/Instrutor Barbeiro – Formador na área de Barbearia Profissional',
        'Educador Técnico Capilar/Instrutor Capilar – Formador na área de Cabeleireiro Profissional',
        'Educador/Instrutor em Terapia Capilar – Formador especialista em tratamentos capilares',
        'Educador/Instrutor Cosmetólogo – Formador especialista em Cabelo e Pele',
        'Educador/Instrutor de Penteados – Professor especializado em penteados técnicos e artísticos',
        'Educador/Instrutor de Cortes e Tendências – Formador especialista em cortes modernos e clássicos',
        'Educador/Instrutor em Colorimetria Capilar – Especialista em coloração e correção de cores',
        'Educador/Instrutor Trancista – Formador especialista em técnicas de tranças afro e modernas',
        'Educador/Instrutor Megahista / Mega Hair – Professor em alongamento capilar',
        'Educador/Instrutor Escovista – Formador especialista em escovas e finalizações',
        'Educador/Instrutor em Prótese Capilar/ Lace – Formador especialista em próteses e perucas',
        'Educador/Instrutor de Alisamento Capilar - Formador e Especialistas em Alisamento',
        'Educador/Instrutor de Assistente/Auxiliar de Cabeleireiro – Formador de assistentes técnicos',
      ],
    },
    {
      id: 'unhas',
      area: 'ÁREA DE UNHAS',
      items: [
        'Educador/Instrutor Manicure e Pedicure – Professor de técnicas de cuidados com unhas',
        'Educador/Instrutor Podologia (Curso Livre) – Formador na área de cuidados dos pés',
        'Educador/Instrutor Nail Design – Professor de design e arte em unhas',
      ],
    },
    {
      id: 'designer',
      area: 'ÁREA DE DESIGNER, MICRO E CÍLIOS',
      items: [
        'Educador/Instrutor em Design de Sobrancelhas',
        'Educador/Instrutor em Extensão de Cílios / Master Lash Designer',
        'Educador/Instrutor em Micropigmentação',
        'Educador/Instrutor em Nanopigmentação',
        'Educador/Instrutor em Microblanding',
        'Educador/Instrutor em Micropigmentação Labial',
        'Educador/Instrutor em Micropigmentação Mamária e Estrias',
      ],
    },
    {
      id: 'maquiagem',
      area: 'ÁREA DE MAQUIAGEM',
      items: [
        'Educador/Instrutor Maquiagem Profissional',
      ],
    },
    {
      id: 'estetica',
      area: 'ÁREA DE ESTÉTICA FACIAL E CORPORAL',
      items: [
        'Educador/Instrutor Estética Facial (sem uso de invasivos)',
        'Educador/Instrutor Estética Corporal (sem uso de invasivos)',
        'Educador/Instrutor em Microagulhamento (derma pen)',
        'Educador/Instrutor em Mesoterapia',
        'Educador/Instrutor em Ozonioterapia',
        'Educador/Instrutor em Biossegurança na Estética',
        'Educador/Instrutor em Depilação/Epilação Profissional',
        'Educador/Instrutor em Laser Profissional',
      ],
    },
    {
      id: 'terapias',
      area: 'ÁREA DE TERAPIAS E SAÚDE INTEGRADA',
      items: [
        'Educador/Instrutor em Drenagem Linfática / Massoterapia Geral',
        'Educador/Instrutor em Massoterapia Tântrica',
        'Educador/Instrutor em Massoterapia Desportiva',
        'Educador/Instrutor em Massoterapia Relaxante',
        'Educador/Instrutor em Quiropraxia',
        'Educador/Instrutor em Terapias Complementares',
        'Educador/Instrutor Terapeuta Holístico',
      ],
    },
    {
      id: 'bronzeamento',
      area: 'ÁREA DE BRONZEAMENTO',
      items: [
        'Educador/Instrutor em Bronzeamento / Personal Bronze / Escultor Bronze',
      ],
    },
    {
      id: 'educacao',
      area: 'ÁREA DE EDUCAÇÃO E GESTÃO',
      items: [
        'Educador/Instrutor em Gestão na Área da Beleza',
        'Educador/Instrutor em Formação Técnica e Pedagógica',
        'Educador/Instrutor Multiprofissional em Saúde, Estética e Beleza',
      ],
    },
    {
      id: 'tatuagem',
      area: 'ÁREA DE TATUAGEM PROFISSIONAL',
      items: [
        'Educador/Instrutor Tatuador Profissional (Reconhecido pela Anvisa desde 2019)',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        [data-scroll-animate] {
          opacity: 0;
          animation: fadeInUp 0.7s ease-out forwards;
        }

        .scroll-visible { opacity: 1 !important; }

        [data-delay="1"] { animation-delay: 0.1s; }
        [data-delay="2"] { animation-delay: 0.2s; }
        [data-delay="3"] { animation-delay: 0.3s; }
        [data-delay="4"] { animation-delay: 0.4s; }
        [data-delay="5"] { animation-delay: 0.5s; }
        [data-delay="6"] { animation-delay: 0.6s; }

        .category-header {
          position: relative;
          overflow: hidden;
        }

        .category-header::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(135deg, rgba(217,119,6,0.08) 0%, rgba(180,83,9,0.04) 100%);
          pointer-events: none;
        }

        .category-header::after {
          content: '';
          position: absolute;
          top: -50%; right: -20%;
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(217,119,6,0.1) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
          pointer-events: none;
        }
      `}</style>

      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-20 pb-20">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-600 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1
              id="hero-title"
              data-scroll-animate
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight scroll-visible drop-shadow-lg"
            >
              Atribuições da Federação
            </h1>
            <p
              id="hero-desc"
              data-scroll-animate
              data-delay="1"
              className="text-lg md:text-xl text-gray-100 mb-8 leading-relaxed scroll-visible drop-shadow-md"
            >
              A Federação dos Profissionais da Beleza e Estética Brasil – Internacional é a única Federação oficial de
              representação, regulamentação, qualificação e fiscalização do setor da beleza e estética no Brasil e no exterior.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'Titulações Títulos Profissionais e Educacionais', value: '100+' },
                { label: 'Escolas Filiadas', value: '5K+' },
                { label: 'Áreas de Atuação', value: '70+' },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  id={`stat-${idx}`}
                  data-scroll-animate
                  data-delay={String(idx + 2)}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-amber-500/30 scroll-visible"
                >
                  <p className="text-2xl font-black text-amber-300">{stat.value}</p>
                  <p className="text-xs md:text-sm text-gray-300">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSÃO E COMPROMISSO */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2
              id="commitment-title"
              data-scroll-animate
              className="text-3xl font-black text-gray-900 mb-8 scroll-visible"
            >
              Nosso Compromisso
            </h2>
            <p
              id="commitment-desc"
              data-scroll-animate
              data-delay="1"
              className="text-lg text-gray-700 leading-relaxed mb-6 scroll-visible"
            >
              Assegurar que cada profissional da beleza e afins exerça sua função com{' '}
              <span className="font-black text-amber-600">dignidade, ética, excelência técnica e responsabilidade social</span>.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                id="oficios-card"
                data-scroll-animate
                data-delay="2"
                className="bg-white rounded-xl p-6 shadow-md border-l-4 border-amber-500 scroll-visible"
              >
                <h3 className="font-black text-gray-900 mb-4 text-lg">Ofício Institucional</h3>
                <ul className="space-y-3">
                  {[
                    'Defender direitos e interesses da classe profissional',
                    'Zelar pela valorização social e econômica',
                    'Promover qualificação contínua',
                    'Estabelecer parâmetros éticos e legais',
                    'Emitir licenças nacionais e internacionais',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      {/* FaCheck original mantido */}
                      <FaCheck className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div
                id="responsabilities-card"
                data-scroll-animate
                data-delay="3"
                className="bg-white rounded-xl p-6 shadow-md border-l-4 border-gray-700 scroll-visible"
              >
                <h3 className="font-black text-gray-900 mb-4 text-lg">Responsabilidades Principais</h3>
                <ul className="space-y-3">
                  {[
                    'Representar profissionais junto autoridades',
                    'Regulamentar exercício profissional',
                    'Emitir certificações e carteiras profissionais',
                    'Fiscalizar prática profissional',
                    'Avaliar formações e experiências',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      {/* FaCheck original mantido */}
                      <FaCheck className="w-4 h-4 text-gray-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIAS PROFISSIONAIS */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <h2
              id="categories-title"
              data-scroll-animate
              className="text-3xl md:text-4xl font-black text-gray-900 mb-4 text-center scroll-visible"
            >
              Categorias de Atuação
            </h2>
            <p
              id="categories-desc"
              data-scroll-animate
              data-delay="1"
              className="text-center text-gray-600 mb-12 max-w-2xl mx-auto scroll-visible"
            >
              A Federação capacita, reconhece e regulamenta as seguintes categorias profissionais dentro do setor de beleza e estética.
            </p>

            <div className="space-y-4">
              {categories.map((category, idx) => (
                <div
                  key={category.id}
                  id={`category-${category.id}`}
                  data-scroll-animate
                  data-delay={String(Math.min(idx + 2, 6))}
                  className="bg-white rounded-xl overflow-hidden border-2 border-amber-200/60 shadow-lg scroll-visible"
                >
                  {/* Header */}
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                    className="category-header w-full bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white flex items-center justify-between hover:opacity-95 transition-all relative"
                  >
                    <div className="flex items-center gap-4 relative z-10">
                      {/* ✅ Imagem direta, sem fundo, sem filter */}
                      <img
                        src={category.icon}
                        alt={category.title}
                        className="w-8 h-8 object-contain flex-shrink-0"
                      />
                      <h3 className="text-xl font-black text-left">{category.title}</h3>
                    </div>
                    {/* FaChevronDown original mantido */}
                    <FaChevronDown
                      className={`w-5 h-5 transition-transform flex-shrink-0 relative z-10 ${
                        expandedCategory === category.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Content */}
                  {expandedCategory === category.id && (
                    <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {category.items.map((item, itemIdx) => (
                          <div
                            key={itemIdx}
                            className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-amber-300/50 transition-colors"
                            style={{ animation: `fadeInUp 0.5s ease-out ${itemIdx * 50}ms both` }}
                          >
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                              {/* FaCheck original mantido */}
                              <FaCheck className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-gray-700 font-medium">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IMPORTÂNCIA DA CERTIFICAÇÃO */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2
              id="certification-title"
              data-scroll-animate
              className="text-3xl font-black mb-6 scroll-visible"
            >
              Importância da Certificação
            </h2>
            <p
              id="certification-desc"
              data-scroll-animate
              data-delay="1"
              className="text-lg text-gray-100 leading-relaxed scroll-visible"
            >
              Todas essas práticas, quando aplicadas por profissionais da beleza, devem seguir critérios técnicos, éticos e sanitários
              com formação reconhecida e regulamentada pela Federação.{' '}
              <span className="font-black text-amber-300">
                A certificação garante segurança para o profissional e credibilidade junto ao cliente.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* LISTA OFICIAL DE TITULAÇÕES */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <h2
              id="titulacoes-title"
              data-scroll-animate
              className="text-3xl md:text-4xl font-black text-gray-900 mb-4 text-center scroll-visible"
            >
              Lista Oficial de Titulações Educacionais
            </h2>
            <p
              id="titulacoes-desc"
              data-scroll-animate
              data-delay="1"
              className="text-center text-gray-600 mb-12 max-w-2xl mx-auto scroll-visible"
            >
              Conheça as titulações e certificações oferecidas e reconhecidas pela Federação em todas as áreas da beleza, estética e saúde integrada.
            </p>

            <div className="space-y-4">
              {titulacoes.map((categoria, idx) => (
                <div
                  key={categoria.id}
                  id={`titulacao-${categoria.id}`}
                  data-scroll-animate
                  data-delay={String(Math.min(idx + 2, 6))}
                  className="bg-white rounded-xl overflow-hidden border-2 border-amber-200/60 shadow-lg scroll-visible"
                >
                  {/* Header */}
                  <div className="category-header w-full bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white flex items-center gap-4 relative">
                    {/* ✅ b9.png direto, sem fundo, sem filter */}
                    <img
                      src="/icons/b9.png"
                      alt="book"
                      className="w-7 h-7 object-contain flex-shrink-0 relative z-10"
                    />
                    <h3 className="text-xl font-black text-left text-white relative z-10">{categoria.area}</h3>
                  </div>

                  {/* Content */}
                  <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                    <div className="space-y-3">
                      {categoria.items.map((item, itemIdx) => (
                        <div
                          key={itemIdx}
                          className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-amber-300/50 hover:shadow-md transition-all"
                          style={{ animation: `fadeInUp 0.5s ease-out ${itemIdx * 50}ms both` }}
                        >
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                            {/* FaCheck original mantido */}
                            <FaCheck className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-gray-700 leading-relaxed">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div
            id="cta-card"
            data-scroll-animate
            className="max-w-2xl mx-auto text-center bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-200 scroll-visible"
          >
            <h3 className="text-2xl font-black text-gray-900 mb-4">Profissional da Beleza?</h3>
            <p className="text-gray-700 mb-6">
              Certifique-se pela Federação e tenha seu profissionalismo reconhecido nacionalmente e internacionalmente.
            </p>
            <a
              href="#"
              className="inline-block px-8 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-black rounded-lg hover:shadow-lg transition-all"
            >
              Solicitar Certificação
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Atribuicoes;